// The constraints on a polygon's or room's ring: edge locks (horizontal /
// vertical), locked lengths and the relations between edges and vertices, solved
// by packages/gcs on every edit; their buttons and lists in the panel, and the
// resizes and drags that keep them. docs/claude/constraints.md.
import {
  type Polygon,
  type ProductInstance,
  type ShapeRelation,
  type Vec2,
  type Wall,
} from '../../shared/types';
import {
  type EdgeRelationType,
  flashToolStatus,
  invalidate,
  pushHistory,
  renderProperties,
  state,
} from './core';
import {
  analyzeSketch,
  buildSketch,
  type Relation as GcsRelation,
  solveSketch,
} from '../gcs';
import { tr } from '../i18n';
import {
  armed,
  type ArmedPick,
  armPick,
  cancelArmed,
  endArmed,
} from './armedPick';
import {
  isJoint,
  polyEdgeLengths,
  rescaleEdgeFeatures,
  wallRuns,
} from '../polyRoom';
import {
  constraintsSatisfied,
  regenerateSauna,
  setEdgeConstraint,
  syncDimensions,
} from '../main';
import {
  polyRoomShim,
  recenterPolyRoom,
} from './vertexEdit';
import { sectionTitle } from './panelRows';
import { followVertices } from '../anchors';

type RingLocks = Array<'h' | 'v' | null> | undefined;
type RingLengths = Array<number | null> | undefined;
/** A ring's full constraint set (orientation locks, length locks, relations). */
export interface RingCons { locks?: RingLocks; lengths?: RingLengths; relations?: ShapeRelation[]; }

/** Per-edge solver constraints from a ring's orientation + length locks. */
function ringEdgeCons(pts: Vec2[], cons: RingCons): Array<{ o: 'h' | 'v' | null; len?: number }> {
  return pts.map((_, k) => {
    const c: { o: 'h' | 'v' | null; len?: number } = { o: cons.locks?.[k] ?? null };
    const L = cons.lengths?.[k];
    if (L != null && L > 0) c.len = L;
    return c;
  });
}

/** Build the solver sketch for a ring + its constraints (relations included). */
export function buildRingSketch(pts: Array<{ x: number; y: number; fixed?: boolean }>, cons: RingCons): ReturnType<typeof buildSketch> {
  return buildSketch({ pts, edgeCons: ringEdgeCons(pts, cons), relations: (cons.relations ?? []) as GcsRelation[], closed: true });
}

/**
 * Solve an edge-length change on a closed ring with the geometric constraint
 * solver: pin the kept endpoint (`anchorIdx`), lock the edited edge to `newLen`,
 * carry the ring's own horizontal/vertical edge locks, and prefer keeping every
 * corner angle (`angleEdges`) so the change is absorbed into the other segments
 * rather than distorting a corner. Returns the solved points, or null if the
 * solve fails / the constraints conflict. Edge `edgeI` joins `pts[edgeI]→next`.
 */
function gcsRingResize(pts: Vec2[], cons: RingCons, edgeI: number, newLen: number, anchorIdx: number): Vec2[] | null {
  const solverPts = pts.map((p, k) => ({ x: p.x, y: p.y, fixed: k === anchorIdx }));
  const built = buildRingSketch(solverPts, cons);
  built.sketch.constraints.push({ id: 'edit-len', type: 'distance', edges: [['v' + edgeI, 'v' + ((edgeI + 1) % pts.length)]], value: newLen });
  const r = solveSketch(built.sketch, { angleEdges: built.edges });
  if (!r.ok || r.conflicting) return null;
  const byId = new Map(r.points.map((p) => [p.id, p]));
  return pts.map((_, k) => { const s = byId.get('v' + k); return s ? { x: s.x, y: s.y } : { x: pts[k].x, y: pts[k].y }; });
}

/**
 * Solve a ring so the straight distance between corners `ai` and `bi` becomes
 * `newLen` — the constraint a user dimension expresses. Pins `ai` (the shape
 * grows from that corner) and keeps the ring's locks/relations and its
 * inter-segment angles. Works for any vertex pair (an edge or a diagonal).
 * Returns the new points, or null on a failed / conflicting solve.
 */
export function gcsDistanceResize(pts: Vec2[], cons: RingCons, ai: number, bi: number, newLen: number): Vec2[] | null {
  if (ai === bi || newLen < 1) return null;
  const solverPts = pts.map((p, k) => ({ x: p.x, y: p.y, fixed: k === ai }));
  const built = buildRingSketch(solverPts, cons);
  built.sketch.constraints.push({ id: 'dim-dist', type: 'distance', edges: [['v' + ai, 'v' + bi]], value: newLen });
  const r = solveSketch(built.sketch, { angleEdges: built.edges });
  if (!r.ok || r.conflicting) return null;
  const byId = new Map(r.points.map((p) => [p.id, p]));
  return pts.map((_, k) => { const s = byId.get('v' + k); return s ? { x: s.x, y: s.y } : { x: pts[k].x, y: pts[k].y }; });
}

/** Solve a ring against its constraints with no edit (used to snap a shape into
 *  place right after a relation is added). Returns the solved ring, or null. */
/** Whether a solved ring is degenerate versus the ring it came from — a segment
 *  reduced below 1 mm, or the outline now crosses itself. The solver can satisfy
 *  a constraint (residual 0) by collapsing an edge (e.g. making two walls collinear
 *  pinches the wall between them to zero); such a result must be rejected rather
 *  than silently deforming the shape. */
function ringDegenerate(before: Vec2[], after: Vec2[]): boolean {
  const n = after.length;
  if (n < 3) return true;
  if (before.length === n) {
    for (let i = 0; i < n; i++) {
      const la = Math.hypot(after[(i + 1) % n].x - after[i].x, after[(i + 1) % n].y - after[i].y);
      const lb = Math.hypot(before[(i + 1) % n].x - before[i].x, before[(i + 1) % n].y - before[i].y);
      if (la < 1 && lb >= 1) return true; // a segment was reduced to under 1 mm
    }
  }
  const cross = (o: Vec2, a: Vec2, b: Vec2): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const crosses = (p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean => {
    const d1 = cross(p3, p4, p1), d2 = cross(p3, p4, p2), d3 = cross(p1, p2, p3), d4 = cross(p1, p2, p4);
    return (d1 > 0) !== (d2 > 0) && (d3 > 0) !== (d4 > 0);
  };
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
    if ((i + 1) % n === j || (j + 1) % n === i) continue; // adjacent edges share a vertex
    if (crosses(after[i], after[(i + 1) % n], after[j], after[(j + 1) % n])) return true;
  }
  return false;
}

export function gcsSolveRing(pts: Vec2[], cons: RingCons): Vec2[] | null {
  const built = buildRingSketch(pts.map((p) => ({ x: p.x, y: p.y })), cons);
  const r = solveSketch(built.sketch, { angleEdges: built.edges });
  if (!r.ok || r.conflicting) return null;
  const byId = new Map(r.points.map((p) => [p.id, p]));
  const solved = pts.map((_, k) => { const s = byId.get('v' + k); return s ? { x: s.x, y: s.y } : { x: pts[k].x, y: pts[k].y }; });
  if (ringDegenerate(pts, solved)) return null; // constraint only satisfiable by collapsing / crossing the outline
  return solved;
}

/** The constraint set of a drawn polygon / a poly-room's ring. */
export function polyRingCons(poly: Polygon): RingCons { return { locks: poly.edgeConstraints, lengths: poly.edgeLengths, relations: poly.relations }; }
export function roomRingCons(inst: ProductInstance): RingCons {
  // A jamb is not a corner: the segments either side of it stay one straight wall.
  // Derived here, never stored, so it can't go stale or be deleted as a relation.
  const n = inst.polygon?.length ?? 0;
  const straight: ShapeRelation[] = [];
  for (let v = 0; v < n; v++) if (isJoint(inst, v)) straight.push({ type: 'collinear', edges: [(v - 1 + n) % n, v] });
  const relations = straight.length ? [...(inst.polygonRelations ?? []), ...straight] : inst.polygonRelations;
  return { locks: inst.polygonConstraints, lengths: inst.polygonEdgeLengths, relations };
}

/** True when a ring carries any real constraint (orientation / length lock or a
 *  relation) — i.e. there's a system for the solver to maintain (else the
 *  single-vertex heuristics are used). */
export function ringHasLocks(cons: RingCons): boolean {
  return (!!cons.locks && cons.locks.some((l) => l != null))
    || (!!cons.lengths && cons.lengths.some((l) => l != null && l > 0))
    || (!!cons.relations && cons.relations.length > 0);
}

/** A human readout of a ring's constraint state, from the solver's DOF analysis:
 *  how many shape degrees of freedom are still free (beyond rigid-body motion),
 *  whether it's fully defined, or whether its constraints conflict. */
function ringConstraintStatus(pts: Vec2[], cons: RingCons): { text: string; conflicting: boolean } {
  const { sketch } = buildRingSketch(pts.map((p) => ({ x: p.x, y: p.y })), cons);
  const a = analyzeSketch(sketch);
  if (a.conflicting) return { text: tr('⚠ Conflicting constraints'), conflicting: true };
  const free = Math.max(0, a.dof - a.gauge);
  if (a.shapeDefined || free === 0) return { text: tr('✓ Shape fully defined'), conflicting: false };
  return { text: tr(free === 1 ? '{n} free degree of freedom' : '{n} free degrees of freedom').replace('{n}', String(free)), conflicting: false };
}

/** Set edge `edge`'s locked length in a per-edge lengths array (creating one of
 *  `count` slots as needed), returning the array — or undefined once every entry
 *  is clear again, so an unconstrained shape stores nothing. */
export function updateEdgeLenArray(current: Array<number | null> | undefined, count: number, edge: number, val: number | null): Array<number | null> | undefined {
  if (val == null && !current) return undefined;
  const arr = current ? current.slice() : new Array<number | null>(count).fill(null);
  while (arr.length < count) arr.push(null);
  arr[edge] = val;
  return arr.some((x) => x != null) ? arr : undefined;
}

/** Keep a length lock in lockstep with the real geometry: if the edge is locked
 *  but the stored value has drifted from the measured length, rewrite it so the
 *  row shows what's actually on the canvas. Returns the same array when in sync. */
export function reconcileEdgeLength(lengths: Array<number | null>, edge: number, actualLen: number): Array<number | null> {
  const r = Math.round(actualLen);
  if (lengths[edge] != null && lengths[edge] !== r) {
    const arr = lengths.slice();
    arr[edge] = r;
    return arr;
  }
  return lengths;
}

/** A "lock length" toggle button reflecting/mutating an edge's length lock. */
export function lengthLockButton(locked: boolean, onToggle: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = tr(locked ? '🔒 Length locked' : '🔓 Lock length');
  b.className = 'toggle' + (locked ? ' active' : '');
  b.style.width = '100%';
  b.style.fontSize = '12px';
  b.style.padding = '6px 4px';
  b.style.margin = '2px 0 4px';
  b.addEventListener('click', onToggle);
  return b;
}

/** Append the solver's live constraint-status line to a shape's edge panel. */
export function appendConstraintStatus(body: HTMLElement, pts: Vec2[], cons: RingCons): void {
  const st = ringConstraintStatus(pts, cons);
  const div = document.createElement('div');
  div.className = 'prop-empty';
  div.style.margin = '4px 0 2px';
  if (st.conflicting) div.style.color = '#e06c6c';
  div.textContent = st.text;
  body.appendChild(div);
}

const EDGE_RELATIONS: Array<{ type: EdgeRelationType; glyph: string; label: string }> = [
  { type: 'parallel', glyph: '∥', label: 'Parallel' },
  { type: 'perpendicular', glyph: '⊥', label: 'Perpendicular' },
  { type: 'equal', glyph: '=', label: 'Equal' },
  { type: 'collinear', glyph: '≡', label: 'Collinear' },
];
function relGlyph(t: string): string { return EDGE_RELATIONS.find((r) => r.type === t)?.glyph ?? t; }
/** A relation's name in the UI's language. */
const relName = (t: string): string => { const label = EDGE_RELATIONS.find((r) => r.type === t)?.label; return label ? tr(label) : t; };

// The shape segment a constraint row is being hovered over — highlighted on the
// canvas so "…to segment N" is legible.
export let hoverConstraintSeg: { shapeId: string; edge: number } | null = null;
function setHoverConstraintSeg(seg: { shapeId: string; edge: number } | null): void { hoverConstraintSeg = seg; invalidate(); }

/** A relation being placed (an armed pick with no click of its own): after a
 *  relation button on `edge`, the next segment selected on the same shape
 *  receives it. It lasts while its shape's segment stays selected; switching
 *  shapes, deselecting, or clicking blank canvas disarms it. */
interface RelationPick extends ArmedPick {
  readonly kind: 'relation';
  readonly shapeId: string;
  readonly edge: number;
  readonly type: EdgeRelationType;
}
function relationPick(): RelationPick | null {
  const p = armed();
  return p?.kind === 'relation' ? p as RelationPick : null;
}
function armRelation(shapeId: string, edge: number, type: EdgeRelationType): void {
  const pick: RelationPick = {
    kind: 'relation', shapeId, edge, type,
    holds: () => state.selectedPolyEdge?.id === shapeId || (state.polyRoomVertexEditId === shapeId && state.polyRoomSelectedEdge != null),
    cancel: () => {},
  };
  armPick(pick);
}

/** Apply a queued edge-pair relation when the user selects the second segment.
 *  Warns (in the status line) and keeps nothing when the relation would conflict. */
export function tryApplyPendingRelation(shapeId: string, edge: number): boolean {
  const p = relationPick();
  if (!p) return false;
  if (p.shapeId !== shapeId) { cancelArmed(); return false; } // switched shapes: disarm
  if (p.edge === edge) return false; // clicked the arming segment: leave it armed
  endArmed();
  const rel: ShapeRelation = { type: p.type, edges: [p.edge, edge] };
  const poly = (state.drawing.polygons || []).find((x) => x.id === shapeId);
  const inst = poly ? null : state.drawing.instances.find((i) => i.id === shapeId);
  if ((poly || inst) && relationDuplicated(poly ? poly.relations : inst?.polygonRelations, rel)) {
    flashToolStatus(tr('Segment {a} is already {rel} to segment {b}.').replace('{a}', String(edge + 1)).replace('{rel}', relName(p.type).toLowerCase()).replace('{b}', String(p.edge + 1)));
    return false;
  }
  const ok = poly ? addPolyRelation(poly, rel) : inst ? addRoomRelation(inst, rel) : false;
  if (!ok && (poly || inst)) flashToolStatus(tr('Can’t make segment {a} {rel} to segment {b} — it would conflict with the existing constraints.').replace('{a}', String(edge + 1)).replace('{rel}', relName(p.type).toLowerCase()).replace('{b}', String(p.edge + 1)));
  return ok;
}

/** The relation buttons for a selected segment: click one, then select the
 *  segment it applies to. Replaces the old set-reference-edge flow. */
export function renderRelationButtons(body: HTMLElement, shapeId: string, edge: number): void {
  body.appendChild(sectionTitle('Add relation'));
  const armedHere = relationPick();
  const pending = armedHere && armedHere.shapeId === shapeId && armedHere.edge === edge ? armedHere.type : null;
  const grid = document.createElement('div');
  grid.style.display = 'grid'; grid.style.gridTemplateColumns = '1fr 1fr'; grid.style.gap = '4px'; grid.style.margin = '2px 0';
  for (const { type, glyph } of EDGE_RELATIONS) {
    const b = document.createElement('button');
    b.textContent = `${glyph} ${relName(type)}`;
    b.className = 'toggle' + (pending === type ? ' active' : '');
    b.style.fontSize = '12px'; b.style.padding = '6px 4px';
    b.addEventListener('click', () => {
      if (pending === type) cancelArmed(); else armRelation(shapeId, edge, type);
    });
    grid.appendChild(b);
  }
  body.appendChild(grid);
  const hint = document.createElement('div'); hint.className = 'prop-empty'; hint.style.margin = '2px 0 6px';
  hint.textContent = pending
    ? tr('Now click the segment to make {rel} to this one.').replace('{rel}', relName(pending).toLowerCase())
    : tr('Pick a relation, then click the segment it applies to.');
  if (pending) hint.style.color = '#4aa3ff';
  body.appendChild(hint);
}

/** The list of constraints on a segment (orientation lock, length lock, edge-pair
 *  relations), each with an ✕ to remove it. Hovering a relation row highlights the
 *  segment it refers to on the canvas. */
export function renderEdgeConstraintsList(body: HTMLElement, shapeId: string, edge: number, cons: RingCons,
  onRemove: { orient: () => void; length: () => void; relation: (index: number) => void }): void {
  body.appendChild(sectionTitle('Constraints'));
  const row = (label: string, remove: () => void, hoverEdge?: number): HTMLElement => {
    const r = document.createElement('div'); r.className = 'prop-row';
    const l = document.createElement('label'); l.textContent = label; l.style.flex = '1';
    const rm = document.createElement('button'); rm.textContent = '✕'; rm.title = tr('Remove constraint'); rm.style.padding = '2px 8px';
    rm.addEventListener('click', remove);
    r.append(l, rm); body.appendChild(r);
    if (hoverEdge != null) {
      r.style.cursor = 'default';
      r.addEventListener('mouseenter', () => setHoverConstraintSeg({ shapeId, edge: hoverEdge }));
      r.addEventListener('mouseleave', () => setHoverConstraintSeg(null));
    }
    return r;
  };
  let any = false;
  const o = cons.locks?.[edge];
  if (o === 'h' || o === 'v') { any = true; row(o === 'h' ? `— ${tr('Horizontal')}` : `| ${tr('Vertical')}`, onRemove.orient); }
  const L = cons.lengths?.[edge];
  if (L != null && L > 0) { any = true; row(tr('🔒 Length {n} mm').replace('{n}', String(Math.round(L))), onRemove.length); }
  (cons.relations ?? []).forEach((rel, index) => {
    if (!('edges' in rel) || !rel.edges.includes(edge)) return;
    any = true;
    const other = rel.edges[0] === edge ? rel.edges[1] : rel.edges[0];
    row(`${relGlyph(rel.type)} ${tr('{rel} to segment {n}').replace('{rel}', relName(rel.type)).replace('{n}', String(other + 1))}`, () => onRemove.relation(index), other);
  });
  if (!any) {
    const empty = document.createElement('div'); empty.className = 'prop-empty'; empty.textContent = tr('No constraints on this segment.');
    body.appendChild(empty);
  }
}

/** Whether two relations are the same constraint on the same segments/vertices
 *  (edge and vertex pairs compared order-insensitively), so an identical one is
 *  never stored twice. */
function sameRelation(a: ShapeRelation, b: ShapeRelation): boolean {
  if (a.type !== b.type) return false;
  if ('edges' in a && 'edges' in b) return (a.edges[0] === b.edges[0] && a.edges[1] === b.edges[1]) || (a.edges[0] === b.edges[1] && a.edges[1] === b.edges[0]);
  if ('verts' in a && 'verts' in b) return (a.verts[0] === b.verts[0] && a.verts[1] === b.verts[1]) || (a.verts[0] === b.verts[1] && a.verts[1] === b.verts[0]);
  if ('vert' in a && 'vert' in b) return a.vert === b.vert;
  return false;
}
function relationDuplicated(existing: ShapeRelation[] | undefined, rel: ShapeRelation): boolean {
  return (existing ?? []).some((r) => sameRelation(r, rel));
}

/** Add a relation to a drawn polygon, snapping it into place. Rejects (and
 *  returns false) when it duplicates an existing one or can't be satisfied, so a
 *  dead relation is never stored. Re-syncs anchored dimensions to the moved geometry. */
export function addPolyRelation(poly: Polygon, rel: ShapeRelation): boolean {
  if (relationDuplicated(poly.relations, rel)) return false; // already present — no duplicate
  const relations = [...(poly.relations ?? []), rel];
  const solved = gcsSolveRing(poly.points, { locks: poly.edgeConstraints, lengths: poly.edgeLengths, relations });
  if (!solved) return false; // unsatisfiable — don't add a conflicting constraint
  pushHistory();
  poly.relations = relations;
  poly.points = solved;
  syncDimensions();
  renderProperties(); invalidate();
  return true;
}
export function removePolyRelation(poly: Polygon, index: number): void {
  pushHistory();
  const next = (poly.relations ?? []).filter((_, i) => i !== index);
  poly.relations = next.length ? next : undefined;
  renderProperties(); invalidate();
}
/** Add a relation to a poly-room, snapping the walls into place. Rejects (and
 *  returns false) on a conflicting constraint. Re-syncs anchored dimensions. */
export function addRoomRelation(inst: ProductInstance, rel: ShapeRelation): boolean {
  if (!inst.polygon) return false;
  if (relationDuplicated(inst.polygonRelations, rel)) return false; // already present — no duplicate
  const relations = [...(inst.polygonRelations ?? []), rel];
  const solved = gcsSolveRing(inst.polygon, { locks: inst.polygonConstraints, lengths: inst.polygonEdgeLengths, relations });
  if (!solved) return false;
  pushHistory();
  inst.polygonRelations = relations;
  inst.polygon = solved;
  recenterPolyRoom(inst); // keep the bounding box centred on the reshaped room
  regenerateSauna(inst);
  syncDimensions();
  renderProperties(); invalidate();
  return true;
}
export function removeRoomRelation(inst: ProductInstance, index: number): void {
  pushHistory();
  const next = (inst.polygonRelations ?? []).filter((_, i) => i !== index);
  inst.polygonRelations = next.length ? next : undefined;
  regenerateSauna(inst);
  renderProperties(); invalidate();
}


/** Lock a polygon edge to an axis and re-solve the whole ring so every other
 *  lock, length and relation stays satisfied (not just a single-vertex nudge).
 *  Reverts and returns false if the new lock conflicts. Pushes history. */
export function makePolyEdgeAxis(poly: Polygon, edge: number, axis: 'h' | 'v'): boolean {
  const prev = poly.edgeConstraints ? poly.edgeConstraints.slice() : undefined;
  setEdgeConstraint(poly, edge, axis);
  const next = poly.edgeConstraints;
  const solved = gcsSolveRing(poly.points, polyRingCons(poly));
  const ok = !!solved && constraintsSatisfied(poly, solved);
  poly.edgeConstraints = prev; // revert; a failed lock leaves the drawing unchanged (no history)
  if (!ok) return false;
  pushHistory(); // snapshot the pre-lock state, only once we know it will change
  poly.edgeConstraints = next;
  poly.points = solved!;
  syncDimensions();
  return true;
}

/** Poly-room counterpart of makePolyEdgeAxis: lock a wall to an axis and re-solve
 *  the room's ring, then refresh the footprint + regenerate. */
export function makeRoomEdgeAxis(inst: ProductInstance, edge: number, axis: 'h' | 'v'): boolean {
  if (!inst.polygon) return false;
  const prev = inst.polygonConstraints ? inst.polygonConstraints.slice() : undefined;
  const sh = polyRoomShim(inst);
  setEdgeConstraint(sh, edge, axis);
  const next = sh.edgeConstraints;
  inst.polygonConstraints = next; // so roomRingCons sees the new lock while solving
  const solved = gcsSolveRing(inst.polygon, roomRingCons(inst));
  inst.polygonConstraints = prev; // revert; a failed lock leaves the drawing unchanged (no history)
  if (!solved) return false;
  pushHistory(); // snapshot the pre-lock state, only once we know it will change
  inst.polygonConstraints = next;
  inst.polygon = solved;
  recenterPolyRoom(inst); // keep the bounding box centred on the reshaped room
  regenerateSauna(inst);
  return true;
}

/** Index of a vertex constraint (angle / coordinate lock) on corner `vert`, or -1. */
export function findVertRelation(rels: ShapeRelation[] | undefined, type: 'angleV' | 'lockX' | 'lockY', vert: number): number {
  return (rels ?? []).findIndex((r) => r.type === type && 'vert' in r && r.vert === vert);
}

/** Change a poly-room vertex constraint's value in place, re-solve, regenerate. */
export function setRoomVertexRelValue(inst: ProductInstance, index: number, value: number): void {
  const rels = inst.polygonRelations;
  const rel = rels?.[index];
  if (!inst.polygon || !rel || !('value' in rel)) return;
  pushHistory();
  rel.value = value;
  const solved = gcsSolveRing(inst.polygon, roomRingCons(inst));
  if (solved) {
    inst.polygon = solved;
    const xs = solved.map((p) => p.x), ys = solved.map((p) => p.y);
    inst.width = Math.max(1, Math.max(...xs) - Math.min(...xs));
    inst.depth = Math.max(1, Math.max(...ys) - Math.min(...ys));
    regenerateSauna(inst);
    syncDimensions();
  }
  renderProperties(); invalidate();
}

/** A "lock" toggle button for a vertex constraint (angle / X / Y); `label` is
 *  shown as given (the caller translates). */
export function vertexLockButton(locked: boolean, label: string, onToggle: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = (locked ? '🔒 ' : '🔓 ') + label;
  b.className = 'toggle' + (locked ? ' active' : '');
  b.style.width = '100%';
  b.style.fontSize = '12px';
  b.style.padding = '6px 4px';
  b.style.margin = '2px 0 4px';
  b.addEventListener('click', onToggle);
  return b;
}

/**
 * Drag vertex `idx` of a closed ring toward `target` with the constraint solver:
 * a soft pull on that vertex while the ring's locks/relations stay hard, so a
 * constrained shape resizes/reshapes as a whole (a locked rectangle stays
 * rectangular) instead of just snapping the one corner. Returns the ring, or null.
 */
export function gcsDragVertex(pts: Vec2[], cons: RingCons, idx: number, target: Vec2): Vec2[] | null {
  // A vertex with a locked X or Y can't move on that axis. The soft drag pull
  // would otherwise tug it a few percent off the lock each solve (compounding
  // over a drag), so pin the target's locked coordinate to the lock value — the
  // pull then agrees with the hard lock and the coordinate holds exactly.
  let tx = target.x, ty = target.y;
  for (const rel of cons.relations ?? []) {
    if ('vert' in rel && rel.vert === idx && rel.value != null) {
      if (rel.type === 'lockX') tx = rel.value;
      else if (rel.type === 'lockY') ty = rel.value;
    }
  }
  const built = buildRingSketch(pts.map((p) => ({ x: p.x, y: p.y })), cons);
  const r = solveSketch(built.sketch, { drag: { point: 'v' + idx, x: tx, y: ty } });
  if (!r.ok) return null;
  const byId = new Map(r.points.map((p) => [p.id, p]));
  return pts.map((_, k) => { const s = byId.get('v' + k); return s ? { x: s.x, y: s.y } : { x: pts[k].x, y: pts[k].y }; });
}

/**
 * Core edge-length resize on a closed ring of points, so the edge (ai→bi)
 * becomes `newLen`. Primary path: the constraint solver (keeps right angles and
 * the ring's h/v locks, absorbing the change into other segments). It pins the
 * corner the heuristic would have kept, so the shape still grows from the same
 * side. Falls back to the single-vertex heuristic when the solver can't help or
 * when dimensioned vertices must be pinned (handled only by the heuristic here).
 * Returns the new points, or null if the edge isn't adjacent / length invalid.
 * Shared by user polygons (which pass `pinPolyId`) and poly-rooms (which don't).
 */
function computeRingEdgeResize(pts: Vec2[], ai: number, bi: number, newLen: number, pinPolyId?: string, editedDimId?: string, cons: RingCons = {}): Vec2[] | null {
  const n = pts.length;
  let i = ai, j = bi;
  if ((i + 1) % n !== j) { [i, j] = [j, i]; }
  if ((i + 1) % n !== j) return null; // not adjacent
  if (newLen < 1) return null;
  const A = pts[i], B = pts[j];
  const dx = B.x - A.x, dy = B.y - A.y;
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;
  const unit = (p: Vec2, q: Vec2): Vec2 => { const vx = q.x - p.x, vy = q.y - p.y; const l = Math.hypot(vx, vy) || 1; return { x: vx / l, y: vy / l }; };
  const isPerp = (a: Vec2, b: Vec2): boolean => Math.abs(a.x * b.x + a.y * b.y) < 0.09; // within ~5° of 90°
  const perpA = isPerp({ x: ux, y: uy }, unit(pts[(i - 1 + n) % n], A)); // wall arriving at A
  const perpB = isPerp({ x: ux, y: uy }, unit(B, pts[(j + 1) % n]));     // wall leaving B
  let moveJ: boolean;
  if (perpA && !perpB) moveJ = true;        // keep the right-angle corner A, move B
  else if (perpB && !perpA) moveJ = false;  // keep B, move A
  else moveJ = Math.max(i, j) === j;        // otherwise move the last-drawn vertex
  const movedIdx = moveJ ? j : i;
  const anchorIdx = moveJ ? i : j;          // the endpoint the solve keeps fixed

  // Use the constraint solver only when the ring actually carries orientation
  // locks: it then *solves* the whole locked system (keeping every locked edge
  // axis-aligned) instead of the current all-or-nothing reject. For a free ring
  // the single-vertex heuristic below is more predictable — a soft angle
  // preference alone doesn't reliably keep an unlocked rectangle rigid. The
  // solver also doesn't model dimension-pinning, so skip it when this ring
  // carries an attached dimension (only the heuristic honours those).
  const hasDimPins = !!pinPolyId && state.drawing.dimensions.some(
    (d) => d.aRef?.shape?.id === pinPolyId || d.bRef?.shape?.id === pinPolyId,
  );
  if (ringHasLocks(cons) && !hasDimPins) {
    const solved = gcsRingResize(pts, cons, i, newLen, anchorIdx);
    if (solved) return solved;
  }

  const oldPos = moveJ ? { x: B.x, y: B.y } : { x: A.x, y: A.y };
  const newPos = moveJ ? { x: A.x + ux * newLen, y: A.y + uy * newLen } : { x: B.x - ux * newLen, y: B.y - uy * newLen };
  const next = pts.map((p) => ({ x: p.x, y: p.y }));
  next[movedIdx] = newPos;
  dragAlignedPolyVertices(pinPolyId ?? '', next, movedIdx, oldPos, newPos, editedDimId);
  return next;
}

/**
 * Resize a polygonal room's wall segment (adjacent drawn vertices `ai`,`bi`) to
 * `newLen`. Uses the same rule as a drawn polygon (carry aligned vertices so a
 * rectangular room stays rectangular), and rejects a resize that would break one
 * of the room's own locked walls. Returns false if not adjacent / invalid.
 */
export function resizePolyEdge(inst: ProductInstance, ai: number, bi: number, newLen: number): boolean {
  const poly = inst.polygon;
  if (!poly) return false;
  const next = computeRingEdgeResize(poly, ai, bi, newLen, undefined, undefined, roomRingCons(inst));
  if (!next) return false;
  if (!constraintsSatisfied(polyRoomShim(inst), next)) return false;
  pushHistory();
  inst.polygon = next;
  recenterPolyRoom(inst); // keep the bounding box centred on the reshaped room
  return true;
}

/**
 * Resize WALL `w` (corner to corner) of a polygonal room to `newLen`. A wall that
 * is one segment is exactly `resizePolyEdge`; one split by jambs moves its far
 * corner with the solver (the jambs stay on the line — derived collinear), keeping
 * the corner that forms a right angle, as `computeRingEdgeResize` does. Either way
 * the wall's figures are then re-laid at the new length: pinned ones and doors
 * hold, free ones — panes included — scale with it.
 */
export function resizePolyWall(inst: ProductInstance, w: number, newLen: number): boolean {
  const run = wallRuns(inst)[w];
  const poly = inst.polygon;
  if (!run || !poly || newLen < 1) return false;
  const lensBefore = polyEdgeLengths(inst);
  if (run.segs.length === 1) {
    if (!resizePolyEdge(inst, run.a, run.b, newLen)) return false;
  } else {
    const n = poly.length;
    const A = poly[run.a], B = poly[run.b];
    const unit = (p: Vec2, q: Vec2): Vec2 => { const l = Math.hypot(q.x - p.x, q.y - p.y) || 1; return { x: (q.x - p.x) / l, y: (q.y - p.y) / l }; };
    const u = unit(A, B);
    const isPerp = (a: Vec2, b: Vec2): boolean => Math.abs(a.x * b.x + a.y * b.y) < 0.09;
    const perpA = isPerp(u, unit(poly[(run.a - 1 + n) % n], A));
    const perpB = isPerp(u, unit(B, poly[(run.b + 1) % n]));
    const keepA = perpA || !perpB;
    const solved = gcsDistanceResize(poly, roomRingCons(inst), keepA ? run.a : run.b, keepA ? run.b : run.a, newLen);
    if (!solved || ringDegenerate(poly, solved) || !constraintsSatisfied(polyRoomShim(inst), solved)) return false;
    pushHistory();
    inst.polygon = solved;
    recenterPolyRoom(inst);
  }
  followVertices(state.drawing, inst.id, () => rescaleEdgeFeatures(inst, lensBefore));
  return true;
}

/** Resize a drawn wall/line edge (adjacent vertices `ai`,`bi`) to `newLen` by
 *  translating the far part of the polyline along the segment (keeping the rest
 *  of the wall rigid). Pushes history. */
export function resizeWallEdge(wall: Wall, ai: number, bi: number, newLen: number): boolean {
  if (Math.abs(ai - bi) !== 1 || newLen < 1) return false; // no-op → no history
  pushHistory();
  return resizeOpenPolylineSegment(wall.points, ai, bi, newLen);
}

/** Stretch an open polyline's segment [ai,bi] (adjacent) to `newLen` in place, by
 *  rigidly translating the vertices after it along the segment direction so the
 *  rest of the polyline keeps its shape. Mutates `pts`; does NOT push history. */
export function resizeOpenPolylineSegment(pts: Vec2[], ai: number, bi: number, newLen: number): boolean {
  const n = pts.length;
  let i = ai, j = bi;
  if (Math.abs(i - j) !== 1) return false; // adjacent along the open polyline
  if (j < i) [i, j] = [j, i];
  if (newLen < 1) return false;
  const A = pts[i], B = pts[j];
  const dx = B.x - A.x, dy = B.y - A.y, L = Math.hypot(dx, dy) || 1;
  const ddx = (A.x + (dx / L) * newLen) - B.x, ddy = (A.y + (dy / L) * newLen) - B.y;
  for (let k = j; k < n; k++) pts[k] = { x: pts[k].x + ddx, y: pts[k].y + ddy };
  return true;
}

/**
 * Resize a drawn polygon's edge (between adjacent vertices `ai` and `bi`) to
 * `newLen` by sliding one vertex along the segment — the same rule as a
 * polygonal room: keep a vertex that forms a right-angle corner with its other
 * edge and move the opposite one; otherwise move the last-drawn vertex.
 */
export function resizePolyShapeEdge(poly: Polygon, ai: number, bi: number, newLen: number, editedDimId?: string): boolean {
  // Same core as a poly-room wall, but pinning vertices that carry a dimension
  // in the move direction and rejecting a break of any locked edge.
  const next = computeRingEdgeResize(poly.points, ai, bi, newLen, poly.id, editedDimId, polyRingCons(poly));
  if (!next) return false;
  if (!constraintsSatisfied(poly, next)) return false;
  pushHistory();
  poly.points = next;
  return true;
}

/** True if a polygon vertex carries a dimension whose line runs parallel to `dir`. */
function vertexHasDimInDir(polyId: string, vertexIndex: number, dir: Vec2, excludeDimId?: string): boolean {
  for (const dm of state.drawing.dimensions) {
    if (dm.id === excludeDimId) continue;
    const onA = dm.aRef?.shape?.id === polyId && dm.aRef.shape.vertex === vertexIndex;
    const onB = dm.bRef?.shape?.id === polyId && dm.bRef.shape.vertex === vertexIndex;
    if (!onA && !onB) continue;
    const ddx = dm.b.x - dm.a.x, ddy = dm.b.y - dm.a.y;
    const dl = Math.hypot(ddx, ddy) || 1;
    const cross = Math.abs((ddx / dl) * dir.y - (ddy / dl) * dir.x);
    if (cross < 0.09) return true; // within ~5° of parallel
  }
  return false;
}

/**
 * After a dimension edit slides one polygon vertex from `oldPos` to `newPos`,
 * translate by the same delta every other vertex that lines up with it across
 * the move (same coordinate along the move axis) and is not pinned by its own
 * dimension running in the move direction.
 */
function dragAlignedPolyVertices(polyId: string, pts: Vec2[], movedIdx: number, oldPos: Vec2, newPos: Vec2, editedDimId?: string): void {
  const dxm = newPos.x - oldPos.x, dym = newPos.y - oldPos.y;
  const dl = Math.hypot(dxm, dym);
  if (dl < 1e-6) return;
  const ux = dxm / dl, uy = dym / dl; // unit move axis
  const TOL = 1; // mm: "aligned" = same coordinate along the move axis
  for (let k = 0; k < pts.length; k++) {
    if (k === movedIdx) continue;
    const proj = (pts[k].x - oldPos.x) * ux + (pts[k].y - oldPos.y) * uy;
    if (Math.abs(proj) > TOL) continue; // lies ahead/behind along the move axis, not aligned
    if (vertexHasDimInDir(polyId, k, { x: ux, y: uy }, editedDimId)) continue; // pinned
    pts[k] = { x: pts[k].x + dxm, y: pts[k].y + dym };
  }
}
